import { ActionIcon, Button, Group, NumberInput, Stack, Text, TextInput } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import {
  DEFAULT_ESCALA_MIN,
  TGuiaNotas,
  TGuiaNotasEntry,
  buildGuiaNotasRecord,
  normalizeGuiaNotas
} from "./rubrica-editor-utils";

type GuiaNotasEditorProperties = {
  value: TGuiaNotas;
  escalaMin: number;
  escalaMax: number;
  onChange: (next: TGuiaNotas) => void;
};

type GuiaNotasEntryRowProperties = {
  entry: TGuiaNotasEntry;
  index: number;
  onUpdate: (index: number, patch: Partial<TGuiaNotasEntry>) => void;
  onRemove: (index: number) => void;
};

/**
 *
 * @param root0
 * @param root0.entry
 * @param root0.index
 * @param root0.onUpdate
 * @param root0.onRemove
 */
function GuiaNotasEntryRow({ entry, index, onUpdate, onRemove }: GuiaNotasEntryRowProperties): JSX.Element {
  const handleNotaChange = useCallback(
    (numberValue: number | string): void => {
      onUpdate(index, { nota: typeof numberValue === "number" ? numberValue : DEFAULT_ESCALA_MIN });
    },
    [index, onUpdate]
  );

  const handleDescricaoChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      onUpdate(index, { descricao: event.currentTarget.value });
    },
    [index, onUpdate]
  );

  const handleRemove = useCallback((): void => {
    onRemove(index);
  }, [index, onRemove]);

  return (
    <Group align="flex-end">
      <NumberInput label="Nota" min={DEFAULT_ESCALA_MIN} value={entry.nota} onChange={handleNotaChange} />
      <TextInput label="Descricao" value={entry.descricao} onChange={handleDescricaoChange} style={{ flex: 1 }} />
      <ActionIcon color="red" variant="subtle" onClick={handleRemove} aria-label="Remover nota">
        <IconTrash size={16} />
      </ActionIcon>
    </Group>
  );
}

/**
 *
 * @param root0
 * @param root0.value
 * @param root0.escalaMin
 * @param root0.escalaMax
 * @param root0.onChange
 */
function GuiaNotasEditor({ value, escalaMin, escalaMax, onChange }: GuiaNotasEditorProperties): JSX.Element {
  const [entries, setEntries] = useState<TGuiaNotasEntry[]>(() => normalizeGuiaNotas(value));

  useEffect(() => {
    setEntries(normalizeGuiaNotas(value));
  }, [value]);

  /**
   *
   * @param nextEntries
   */
  function updateEntries(nextEntries: TGuiaNotasEntry[]): void {
    setEntries(nextEntries);
    onChange(buildGuiaNotasRecord(nextEntries));
  }

  /**
   *
   */
  function handleAdd(): void {
    const nextEntries = [...entries, { nota: escalaMin, descricao: "" }];
    updateEntries(nextEntries);
  }

  /**
   *
   * @param index
   */
  function handleRemove(index: number): void {
    const nextEntries = entries.filter((_, currentIndex) => currentIndex !== index);
    updateEntries(nextEntries);
  }

  /**
   *
   * @param index
   * @param patch
   */
  function handleUpdate(index: number, patch: Partial<TGuiaNotasEntry>): void {
    const nextEntries = entries.map((entry, currentIndex) => (currentIndex === index ? { ...entry, ...patch } : entry));
    updateEntries(nextEntries);
  }

  /**
   *
   */
  function handleGenerate(): void {
    const start = Math.min(escalaMin, escalaMax);
    const end = Math.max(escalaMin, escalaMax);
    const startValue = Math.ceil(start);
    const endValue = Math.floor(end);

    if (!Number.isFinite(startValue) || !Number.isFinite(endValue) || startValue > endValue) {
      return;
    }

    const generated: TGuiaNotasEntry[] = [];
    for (let nota = startValue; nota <= endValue; nota += 1) {
      generated.push({ nota, descricao: "" });
    }
    updateEntries(generated);
  }

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="sm" fw={600}>
          Guia de notas
        </Text>
        <Group gap="xs">
          <Button size="xs" variant="default" onClick={handleGenerate}>
            Gerar pela escala
          </Button>
          <Button size="xs" variant="outline" onClick={handleAdd} leftSection={<IconPlus size={14} />}>
            Adicionar nota
          </Button>
        </Group>
      </Group>

      {entries.length === 0 ? (
        <Text size="sm" c="dimmed">
          Nenhuma nota cadastrada.
        </Text>
      ) : (
        entries.map((entry, index) => (
          <GuiaNotasEntryRow
            key={`${entry.nota}-${index}`}
            entry={entry}
            index={index}
            onUpdate={handleUpdate}
            onRemove={handleRemove}
          />
        ))
      )}
    </Stack>
  );
}

export default GuiaNotasEditor;
